import { Injectable } from '@angular/core';
import { Logger, LoggerFactory } from '../core/logger.service';
import { TriggerbeeApi } from '../core/triggerbee/triggerbee-requester.service';
import { HttpRequestDecorator } from '../core/triggerbee/http.request.decorator';
import { BehaviorSubject, Observable, ReplaySubject, Subscription, timer } from 'rxjs';
import { BrandVariable, BrandVariableText } from './brand-variable';
import { TypeText, WidgetComponentSubType, WidgetComponentType } from 'app/campaign/widgets.model';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';

@Injectable({
  providedIn: 'root',
})
export class BrandVariablesService {
  private readonly logger: Logger;
  private latestBrandVariable?: ReplaySubject<BrandVariable>;
  private cacheResetTimer?: Subscription;
  public showVerifyEmailMessage = new BehaviorSubject<boolean>(undefined);
  constructor(
    loggerFactory: LoggerFactory,
    private http: HttpRequestDecorator,
  ) {
    this.logger = loggerFactory.get('BrandVariablesService');
  }

  private getGatewayResource(subResource?: string): string {
    return this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/` + (subResource || '');
  }

  public getLatest(): Observable<BrandVariable> {
    if (!this.latestBrandVariable) {
      this.latestBrandVariable = new ReplaySubject<BrandVariable>(1);
      this.http.get(this.getGatewayResource(`brand-variables/get-latest`)).subscribe(this.latestBrandVariable);
      // Clear the cache after 1 minute
      this.cacheResetTimer = timer(60 * 1000).subscribe(() => this.clearCache());
    }
    return this.latestBrandVariable.asObservable();
  }

  public getDefaultSettings(): Observable<BrandVariable> {
    return this.http.get(this.getGatewayResource(`brand-variables/get-default`));
  }

  public create(brandVariable: BrandVariable): Observable<BrandVariable> {
    this.clearCache();
    return this.http.post(this.getGatewayResource(`brand-variables`), brandVariable);
  }

  private clearCache(): void {
    this.latestBrandVariable = undefined;
    this.cacheResetTimer?.unsubscribe();
  }

  public getBrandVariableByComponent(component: any, brandVariables: BrandVariable, typeText: string): any {
    if (!component) return;
    const type =
      component.type === WidgetComponentType.Button && component.subType !== WidgetComponentSubType.ButtonHide
        ? 'button'
        : component?.textProperties?.text?.includes('<h') &&
            component.type === WidgetComponentType.Text &&
            typeText === TypeText.TextProperties
          ? 'headline'
          : !component?.textProperties?.text?.includes('<h') &&
              component.type === WidgetComponentType.Text &&
              typeText === TypeText.TextProperties
            ? 'text'
            : (component.type === WidgetComponentType.BulletList ||
                  component.type === WidgetComponentType.InputText ||
                  component.type === WidgetComponentType.Dropdown ||
                  component.type === WidgetComponentType.Date ||
                  component.type === WidgetComponentType.Share ||
                  component.type === WidgetComponentType.ButtonList ||
                  component.type === WidgetComponentType.InternationalPhone) &&
                typeText === TypeText.TextProperties
              ? 'input'
              : (component.type === WidgetComponentType.BulletList ||
                    component.type === WidgetComponentType.InputText ||
                    component.type === WidgetComponentType.Dropdown ||
                    component.type === WidgetComponentType.Date ||
                    component.type === WidgetComponentType.Share ||
                    component.type === WidgetComponentType.ButtonList ||
                    component.type === WidgetComponentType.InternationalPhone) &&
                  typeText === TypeText.Label
                ? 'label'
                : component.type === WidgetComponentType.Memory ||
                    component.type === WidgetComponentType.SpinTheWheel ||
                    component.type === WidgetComponentType.Checkbox
                  ? 'text'
                  : null;
    return brandVariables ? brandVariables[type] : null;
  }

  public extractBrandVariablesMetricsFromUnit(brandVariables) {
    if (!brandVariables) return;
    const brandVar = JSON.parse(JSON.stringify(brandVariables));
    for (const metricKey in brandVar) {
      if (
        brandVar[metricKey] &&
        typeof brandVar[metricKey] === 'string' &&
        (brandVar[metricKey].endsWith('px') ||
          brandVar[metricKey].endsWith('em') ||
          brandVar[metricKey].endsWith('rem'))
      ) {
        brandVar[metricKey] = String(WidgetComponentHelpers.extractNumberFromStringWithUnit(brandVar[metricKey]));
      }
    }
    return brandVar;
  }

  public calculateRealHeaderFontSize(
    brandVarItemWithoutUnits: BrandVariableText,
    component: any,
    typeText: TypeText,
  ): BrandVariableText {
    if (
      component?.textProperties?.text?.includes('<h') &&
      component.type === WidgetComponentType.Text &&
      typeText === TypeText.TextProperties
    ) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(component?.textProperties?.text, 'text/html');
      const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
      let modifyedHeadings = brandVarItemWithoutUnits;

      headings.forEach((h) => {
        switch (h.tagName) {
          case 'H1':
            modifyedHeadings = { ...brandVarItemWithoutUnits };
            break;
          case 'H2':
            modifyedHeadings = {
              ...brandVarItemWithoutUnits,
              fontSize: String(Math.round(+brandVarItemWithoutUnits.fontSize * 0.8 * 10) / 10),
            };
            break;
          case 'H3':
            modifyedHeadings = {
              ...brandVarItemWithoutUnits,
              fontSize: String(Math.round(+brandVarItemWithoutUnits.fontSize * 0.64 * 10) / 10),
            };
            break;
          case 'H4':
            modifyedHeadings = {
              ...brandVarItemWithoutUnits,
              fontSize: String(Math.round(+brandVarItemWithoutUnits.fontSize * 0.512 * 10) / 10),
            };
            break;
          case 'H5':
            modifyedHeadings = {
              ...brandVarItemWithoutUnits,
              fontSize: String(Math.round(+brandVarItemWithoutUnits.fontSize * 0.41 * 10) / 10),
            };
            break;
          case 'H6':
            modifyedHeadings = {
              ...brandVarItemWithoutUnits,
              fontSize: String(Math.round(+brandVarItemWithoutUnits.fontSize * 0.33 * 10) / 10),
            };
            break;
          default:
            modifyedHeadings;
            break;
        }
      });
      return modifyedHeadings;
    } else {
      return brandVarItemWithoutUnits;
    }
  }
}
